import { readFeature, writeFeature } from '@abaco/map-web-module/src/modules/map-web-module/LayerBuilder';
import { HTTPClient, propsToDate, propsToDatePaged } from '@abaco/web-common/src/HTTP';
import { PagedResults, PagingParams } from '@abaco/web-common/src/Paging';
import { Track, TrackFilter, TrackPoint, TrackPossibleDimension, TrackSegment } from '../models/Track';
import LineString from "ol/geom/LineString";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";

export default class TrackApi {
    private http: HTTPClient;
    private rootPath: string;

    constructor(http: HTTPClient, rootPath: string) {
        this.http = http;
        this.rootPath = rootPath;
    }

    public async getTracks(pk_cuaa: number, currentCampaign: number, trackFilter: TrackFilter): Promise<Track[]>{
        const res = await this.getPaginatedTracks(pk_cuaa, currentCampaign, trackFilter, {offset: 0, count: 300});
        return res.records;
    }

    public async getPaginatedTracks(pk_cuaa: number, currentCampaign: number, trackFilter: TrackFilter, pagingParams: PagingParams): Promise<PagedResults<Track>> {
        const filters: string[] = [];

        if (trackFilter.track_id) {
            filters.push(`track_id eq ${trackFilter.track_id}`);
        }
        if (trackFilter.plot_id) {
            filters.push(`plot_id eq ${trackFilter.plot_id}`);
        }
        if (trackFilter.from) {
            filters.push(`execution_date ge ${trackFilter.from.getTime()}`);
        }
        if (trackFilter.to) {
            filters.push(`execution_date le ${trackFilter.to.getTime()}`);
        }

        const url = `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${currentCampaign}/tracks`;
        const params = {
            offset: pagingParams.offset,
            count: pagingParams.count,
            filter: filters,
            sort: `-execution_date`
        }

        const res = await this.http.get(url, { params });
        propsToDatePaged(res.data, ["track_details.start_track", "track_details.end_track"])
        return res.data;
    }

    public async getGenericTracks(pk_cuaa: number, trackFilter: TrackFilter): Promise<Track[]>{
        const res =  await this.getPaginatedGenericTracks(pk_cuaa, trackFilter, {offset: 0, count: 300});
        return res.records;
    }

    public async getPaginatedGenericTracks(pk_cuaa: number, trackFilter: TrackFilter, pagingParams: PagingParams): Promise<PagedResults<Track>>{
        const filters: string[] = [];

        if (trackFilter.plot_id) {
            filters.push(`plot_id eq ${trackFilter.plot_id}`);
        }
        if (trackFilter.from) {
            filters.push(`start_track ge ${trackFilter.from.getTime()}`);
        }
        if (trackFilter.to) {
            filters.push(`start_track le ${trackFilter.to.getTime()}`);
        }

        const url = `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/tracks_all`;
        const params = {
            offset: pagingParams.offset,
            count: pagingParams.count,
            filter: filters,
            sort: `-start_track`
        }
        const res = await this.http.get(url, { params });
        propsToDatePaged(res.data, ["track_details.start_track", "track_details.end_track"])
        return res.data;
    }

    public async getTrackPoints(trackId: string): Promise<TrackPoint[]> {
        // trackId = "mjd_trk_209781_8cf907c9-d2f0-467e-bd59-996ebaa29893_MjA5NzgxXzYwNjJjYWMzYWNjZTFiMTQ3NDQ3ZjM2ZQ";
        // trackId = "TS->path-id:1104-ac909f30-5411-4137-a3eb-96ae07d68b36";
        const url = `${this.rootPath}/v1/plots/tracks/${trackId}/points`;
        const res = await this.http.get<TrackPoint[]>(url);
        res.data.forEach(tr => propsToDate(tr, ['dt_position']))
        return res.data;
    }


    // public async getTrackSegments(trackId: string): Promise<TrackSegment[]>{
    //     const url = `${this.rootPath}/v1/plots/tracks/${trackId}/segments`;
    //     const res = await this.http.get<TrackSegment[]>(url);
    //     res.data.forEach(tr => propsToDate(tr, ['start_time', 'stop_time']));
    //     return res.data;
    // }

    public async getTrackSegments(trackId: string, selectedDimension: string): Promise<TrackSegment[]>{
        const url = `/sitiagri-rest-api/api_basic/v1/plots/tracks/${trackId}/attrs/${selectedDimension}/segments_from_points`;
        const res = (await this.http.get<TrackSegment[]>(url)).data;

        if(res.length >= 1){
            const firstSegment = res[0];
            const lastSegment = res[res.length - 1];
            const firstPoint = new Feature(new Point((readFeature({wkt: firstSegment.geometry.wkt_geometry, id: "start"}).getGeometry() as LineString).getFirstCoordinate()));
            const lastPoint = new Feature(new Point((readFeature({wkt: lastSegment.geometry.wkt_geometry, id: "end"}).getGeometry() as LineString).getLastCoordinate()));
            res.unshift({attr: firstSegment.attr, geometry: {srs: firstSegment.geometry.srs, wkt_geometry: writeFeature(firstPoint)}});
            res.push({attr: lastSegment.attr, geometry: {srs: lastSegment.geometry.srs, wkt_geometry: writeFeature(lastPoint)}});
        }
        return res;
    }

    public async getPossibleDimensions(trackId: string): Promise<string[]>{
        const url = `/sitiagri-rest-api/api_basic/v1/plots/tracks/${trackId}/attrs`;
        const res = await this.http.get<TrackPossibleDimension>(url);

        const result: string[] = [];
        Object.entries(res.data).forEach((entry) => {
            if(entry[1]){
                result.push(entry[0])
            }
        })
        return result;
    }

    public async canViewTracks(pkCuaa: number) {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/user_registry/module_competencies/BEST_PRACTICE/SOGG_TRACKS_LINK`, {
            params: {
                subject_id: pkCuaa
            }
        })

        return res.data.result
    }

}
